import { Dispatch, useCallback, useEffect } from "react";
import Hls from "hls.js";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { usePlayerStore } from "../../store/usePlayerStore";
import { AudioTrack } from "../../types/common";

const useSetupNativeHls = ({
  setIsMediaAttached,
  usingServiceScheduleTracks,
  video
}: {
  setIsMediaAttached: Dispatch<boolean>;
  usingServiceScheduleTracks: boolean;
  video?: HTMLVideoElement;
}) => {
  const { getState, useStoreState } = usePlayerStore();

  const audioTracks = useStoreState.audioTracks();
  const selectedAudioTrackId = useStoreState.selectedAudioTrackId();

  const setAudioTracks = useStoreState.setAudioTracks();
  const setError = useStoreState.setError();
  const setIsPlayerSetupDone = useStoreState.setIsPlayerSetupDone();

  const { manifestUrl } = useMediaContext();
  const { preload } = useEmbedParametersContext();

  const onAudioTracksChange = useCallback(() => {
    if (video) {
      const tracks: AudioTrack[] = [];
      for (const track of Array.from(video.audioTracks)) {
        const newTrack = {
          id: parseInt(track.id, 10),
          label: track.label,
          language: track.language
        };
        tracks.push(newTrack);
      }
      setAudioTracks(tracks);
    }
  }, [setAudioTracks, video]);

  useEffect(() => {
    if (
      selectedAudioTrackId !== undefined &&
      selectedAudioTrackId !== -1 &&
      video?.audioTracks &&
      !Hls.isSupported()
    ) {
      // Set selected audio track as enabled
      for (const track of Array.from(video.audioTracks) as MediaStreamTrack[]) {
        track.enabled = parseInt(track.id, 10) === selectedAudioTrackId;
      }
    }
  }, [audioTracks, selectedAudioTrackId, video]);

  useEffect(() => {
    if (
      !Hls.isSupported() &&
      video?.audioTracks &&
      !usingServiceScheduleTracks
    ) {
      video.audioTracks.addEventListener("addtrack", onAudioTracksChange);
      video.audioTracks.addEventListener("change", onAudioTracksChange);

      return () => {
        video.audioTracks.removeEventListener("addtrack", onAudioTracksChange);
        video.audioTracks.removeEventListener("change", onAudioTracksChange);
      };
    }
  }, [onAudioTracksChange, usingServiceScheduleTracks, video]);

  return (): void => {
    if (video) {
      const html5ErrorCallback = () =>
        setError({
          code: video.error?.code,
          message: video.error?.message
        });
      video.addEventListener("error", html5ErrorCallback);
      if (getState().currentTime !== -1) {
        // Fix for iOS Safari
        video.addEventListener(
          "canplay",
          () => (video.currentTime = getState().currentTime),
          { once: true }
        );
      }
      video.setAttribute("preload", preload || "none");
      const originalPlaybackRate = video.playbackRate;
      if (manifestUrl) {
        video.src = manifestUrl;
      }
      video.playbackRate = originalPlaybackRate;
      setIsMediaAttached(true);
      setIsPlayerSetupDone(true);
    }
  };
};

export default useSetupNativeHls;
